import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { SKETCH_URL } from '../app-urls';
import { Subscription } from 'rxjs';
import { CURRENT_STATE_PROJECT_KEY, MODEL_TYPE } from '../common/linqset-model';
import { MODEL_FUNCTION, ModelDetails, Organisation } from '../common/organisation';
import { LensKey } from '../lenses/lens';
import { LensSwitcherComponent } from '../lenses/lens-switcher.component';
import { MaterialModule } from '../material/material.module';
import { GroupItem, ItemSelection } from '../ui-components/group-selection/group-selection.component';
import { UIComponentsModule } from '../ui-components/ui-components.module';
import { AnalysisScope, modelsInScope, ScopeService } from './scope';
import { InsightsStatusComponent } from './insights-status.component';

// The bar every analysis view shares: which view (Overview, Actions, People, Systems) and what is looked at (the
// workspace and the models). Changing either here changes it for every view, so moving between
// views never changes what is being looked at. With one model in scope, a view that draws a graph can show the LINQ
// model itself (every action, information node, system and role) in place of its summary
@Component({
    selector: 'app-analysis-header',
    standalone: true,
    imports: [FormsModule, MaterialModule, UIComponentsModule, LensSwitcherComponent, InsightsStatusComponent],
    template: `
        <!-- in a project the bar takes the project's colours, as the model screen's toolbar does -->
        <div class="bar" [class.project]="inProject" data-testid="analysis-header">
            <app-lens-switcher id="analysis-views" class="views" [current]="view" [organisationKey]="organisation?._key || ''"></app-lens-switcher>
            @if (organisation && scope) {
            <div class="scope">
                <mat-form-field id="analysis-workspace" color="accent" subscriptSizing="dynamic" class="workspace">
                    <mat-label>Workspace</mat-label>
                    <mat-select [value]="scope.workspaceKey" [panelWidth]="null" (selectionChange)="workspaceChanged($event.value)" data-testid="scope-workspace">
                        @for (w of workspaces; track w.key) {<mat-option [value]="w.key">{{ w.name }}</mat-option>}
                    </mat-select>
                </mat-form-field>
                <app-group-selection #models id="analysis-models" class="models" [groups]="modelGroups" (openedEvent)="modelsChanged($event)" label="Models" [multiple]="true"
                    placeholder="All models" hint=""></app-group-selection>
                @if (singleModel && canShowFullModel) {
                <button id="analysis-full-model" mat-stroked-button color="accent" (click)="toggleFullModel()" [matTooltip]="fullModel ? 'Back to the summary view' : 'Show the LINQ model, with all its actions, information, systems and roles'"
                    [attr.aria-pressed]="fullModel" data-testid="scope-full-model"><mat-icon>{{ fullModel ? 'account_tree' : 'schema' }}</mat-icon>{{ fullModel ? 'View summary' : 'View full model' }}</button>
                }
                @if (singleModel) {
                <button id="analysis-edit-model" mat-stroked-button color="accent" (click)="editModel()" matTooltip="Open the model in the modelling screen" data-testid="scope-edit-model">
                    <mat-icon>edit</mat-icon>Edit model</button>
                }
            </div>
            }
        </div>
        <app-insights-status [organisationKey]="organisation?._key" [workspaceKey]="scope?.workspaceKey"></app-insights-status>
    `,
    styles: [`
        :host { display: block; }
        .bar { display: flex; align-items: center; gap: 16px; flex-wrap: wrap; padding: 8px 16px; background: var(--bg, #f3f4f6); border-bottom: 1px solid var(--line, #e5e7eb); }
        .bar.project { background: var(--amber-soft, #fdf3dd); border-bottom: 2px solid var(--amber, #E8A400); }
        .views { flex: 1 1 100%; max-width: 760px; }
        /* the workspace, models and their buttons together, at the right of the bar */
        .scope { display: flex; align-items: center; justify-content: flex-end; gap: 12px; flex-wrap: wrap; flex: 1 1 auto; margin-left: auto; }
        /* room for a project's name, which can be long; the list widens to show the longest */
        .workspace { flex: 0 1 320px; min-width: 200px; max-width: 100%; }
        .models { width: 260px; }
        .scope button { white-space: nowrap; }
    `],
})
export class AnalysisHeaderComponent implements OnChanges, OnDestroy {
    get inProject() {
        return !!this.scope && this.scope.workspaceKey !== CURRENT_STATE_PROJECT_KEY;
    }

    @Input() organisation?: Organisation;
    @Input() view: LensKey = 'overview';
    @Input() canShowFullModel = false;   // the view can show the full model in place of its summary
    @Input() fullModel = false;
    @Output() fullModelChange = new EventEmitter<boolean>();

    scope?: AnalysisScope;
    workspaces: { key: string; name: string }[] = [];
    modelGroups: GroupItem[] = [];
    singleModel?: ModelDetails;
    private sub?: Subscription;

    constructor(private scopeService: ScopeService, private router: Router) {}

    // the model in the modelling screen, in the workspace looked at
    editModel() {
        if (!this.organisation || !this.singleModel || !this.scope) return;
        void this.router.navigateByUrl(SKETCH_URL(this.organisation._key, this.singleModel._key, MODEL_TYPE.PROCESS, this.scope.workspaceKey));
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['organisation'] && this.organisation) {
            this.sub?.unsubscribe();
            this.sub = this.scopeService.scope$.subscribe(scope => this.show(scope));
        }
    }

    ngOnDestroy() {
        this.sub?.unsubscribe();
    }

    private show(scope: AnalysisScope) {
        if (!this.organisation) return;
        const organisation = this.organisation;
        this.scope = scope;
        this.workspaces = [{ key: CURRENT_STATE_PROJECT_KEY, name: 'Current state' }, ...(organisation.projects || []).filter(p => p.role).map(p => ({ key: p._key, name: p.name }))];
        const models = modelsInScope(scope, organisation);
        this.singleModel = models.length === 1 ? models[0] : undefined;
        // the full model is one model's; with more or fewer in scope, back to the summary
        if (!this.singleModel && this.fullModel) this.fullModelChange.emit(false);
        this.modelGroups = this.groups(scope, organisation);
    }

    // the models of the workspace by name, and the functions and tags to choose them by, with the scope's picked
    private groups(scope: AnalysisScope, organisation: Organisation): GroupItem[] {
        const functions: MODEL_FUNCTION[] = [];
        const changed = new GroupItem('changed', undefined, 'Changed in this project', undefined, false, undefined, []);
        const models = new GroupItem('models', undefined, 'Models', undefined, false, undefined, []);
        organisation.models
            .filter(m => (m.projectKey === scope.workspaceKey || m.projectKey === CURRENT_STATE_PROJECT_KEY) && !m.deletedDate && m.role)
            .sort((a, b) => a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1)
            .forEach(m => {
                if (!functions.includes(m.function)) functions.push(m.function);
                const inProject = scope.workspaceKey !== CURRENT_STATE_PROJECT_KEY && (m.projects || []).includes(scope.workspaceKey);
                const group = inProject ? changed : models;
                group.items!.push(new GroupItem(m._key, group._key, m.name, undefined, scope.modelKeys.includes(m._key)));
            });
        const groups = scope.workspaceKey !== CURRENT_STATE_PROJECT_KEY ? [changed, models] : [models];
        groups.push(new GroupItem('functions', undefined, 'Model functions', undefined, false, undefined, functions.map(f => new GroupItem(f, 'functions', f, undefined, scope.modelFunctions.includes(f)))));
        const tags = organisation.modelTags?.tags || [];
        if (tags.length) groups.push(new GroupItem('tags', undefined, 'Model tags', undefined, false, undefined, tags.map(t => new GroupItem(t._key, 'tags', t.name, undefined, scope.modelTags.includes(t._key)))));
        return groups;
    }

    workspaceChanged(workspaceKey: string) {
        if (!this.scope || workspaceKey === this.scope.workspaceKey) return;
        // the models picked stay picked across workspaces; functions and tags still apply
        this.scopeService.set({ ...this.scope, workspaceKey });
    }

    // the list closed: what it holds is the scope
    modelsChanged(event: { open: boolean; selection: ItemSelection[] }) {
        if (event.open || !this.scope) return;
        const modelKeys: string[] = [], modelFunctions: MODEL_FUNCTION[] = [], modelTags: string[] = [];
        event.selection.forEach(i => {
            if (i.item.parentKey === 'functions') modelFunctions.push(i.item._key as MODEL_FUNCTION);
            else if (i.item.parentKey === 'tags') modelTags.push(i.item._key);
            else modelKeys.push(i.item._key);
        });
        const same = (a: string[], b: string[]) => a.length === b.length && a.every(k => b.includes(k));
        if (same(modelKeys, this.scope.modelKeys) && same(modelFunctions, this.scope.modelFunctions) && same(modelTags, this.scope.modelTags)) return;
        this.scopeService.set({ ...this.scope, modelKeys, modelFunctions, modelTags });
    }

    toggleFullModel() {
        this.fullModelChange.emit(!this.fullModel);
    }
}
